import React, { FC, useState, useMemo } from 'react';
import { Autocomplete, Box, Chip, Grid, Paper, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useGetAllProductCategoriesQuery, useGetAllProductsQuery, useGetSuburbStatusQuery } from '@services';
import { LoadingSpinner } from '@components';
import { generateUniqueKey, toCurrency } from '@helpers';
import { Product, ProductCategory } from '@models';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash.debounce';
import { getZoneId } from '@helpers';

type Props = {
  searchSuburb: string;
};

type ProductsTableList = {
  id: number;
  sku: string;
  name: string;
  price: number;
  categories: string[];
}[];

const AvaSuburbStatus: FC<Props> = ({ searchSuburb }) => {
  const { data: products, isFetching } = useGetAllProductsQuery();
  const { data: categories } = useGetAllProductCategoriesQuery();
  const { data: suburbProductsStatus } = useGetSuburbStatusQuery({
    suburb: searchSuburb,
  });
  const zoneId = getZoneId(categories);

  const uniqueCategories = useMemo(() => {
    // Subset of categories - filters our Menu1, Zones and subcategories of Zones.
    const filteredCategories = categories?.filter((c) => {
      return c.parent_id !== zoneId && c.id !== zoneId && c.id !== 40;
    });

    if (filteredCategories) {
      const categorySet = new Set<string>();

      filteredCategories.forEach((category) => categorySet.add(category.name));

      return categorySet;
    }
  }, [categories, zoneId]);

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const getCategories = (product: Product) => {
    return product.categories.reduce((productCategories, id) => {
      const category = categories?.find((c) => c.id === id && c.parent_id !== zoneId);
      return category ? [...productCategories, category] : productCategories;
    }, [] as ProductCategory[]);
  };

  const getCategoryNameById = (categoryId: number) => {
    const category = categories?.find((c) => {
      return c.id === categoryId;
    });

    if (category) {
      return category.name;
    } else {
      return 'No Category';
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'categories',
      headerName: 'Categories',
      flex: 1,
      renderCell: ({ value }) =>
        value?.map((category: string) => <Chip key={generateUniqueKey()} label={category} size="small" sx={{ mr: 1 }} />),
    },
    { field: 'price', headerName: 'Price', flex: 1, valueFormatter: ({ value }) => toCurrency(Number(value)) },
  ];

  const [searchText, setSearchText] = useState('');

  const rows = products?.reduce((products, product) => {
    const categories = getCategories(product).map(({ id }) => getCategoryNameById(id));

    if (!selectedCategories.length || selectedCategories.find((value) => categories.includes(value))) {
      products.push({
        id: product.id,
        sku: product.sku,
        name: product.name,
        price: product.price,
        categories,
      });
    }

    return products;
  }, [] as ProductsTableList);

  const handleSearchTextChangeDebounced = useMemo(() => debounce((text: string) => setSearchText(text), 300), []);

  return isFetching ? (
    <LoadingSpinner isOpen={isFetching} />
  ) : (
    <Box paddingLeft={1} paddingTop={2} data-testid="ava-suburb-status">
      <Grid container justifyContent="flex-start" sx={{ mb: 1 }} spacing={2}>
        <Grid item xs={6}>
          <Autocomplete
            multiple
            options={(uniqueCategories && Array.from(uniqueCategories)) || []}
            getOptionLabel={(option: string) => option}
            filterSelectedOptions
            onChange={(e, categories) => {
              setSelectedCategories(categories);
            }}
            renderInput={(params) => <TextField {...params} label="Filter by categories" placeholder="Categories" />}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            variant="outlined"
            placeholder="Search for product..."
            fullWidth
            onChange={(e) => handleSearchTextChangeDebounced(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" />,
            }}
          />
        </Grid>
      </Grid>
      <Paper sx={{ height: '90vh' }}>
        {rows && (
          <DataGrid
            sortingOrder={['desc', 'asc']}
            rows={rows}
            columns={columns}
            disableSelectionOnClick
            disableColumnSelector
            disableColumnFilter
            filterModel={{
              items: [
                {
                  columnField: 'name',
                  operatorValue: 'contains',
                  value: searchText,
                },
              ],
            }}
            getRowClassName={(params) => {
              let className = '';
              const sku = params.row.sku;

              if (suburbProductsStatus?.oos.includes(sku) || suburbProductsStatus?.snoozed.includes(sku)) {
                className = 'oos';
              }

              if (suburbProductsStatus?.deranged.includes(sku)) {
                className = 'oos deranged';
              }

              return className;
            }}
            sx={{
              '& .MuiDataGrid-row.oos': {
                opacity: 0.5,
              },
              '& .MuiDataGrid-row.oos:hover': {
                backgroundColor: 'unset',
              },
              '& .MuiDataGrid-row.deranged': {
                position: 'relative',
              },
              '& .MuiDataGrid-row.deranged:hover': {
                backgroundColor: 'unset',
              },
              '& .MuiDataGrid-row.deranged:before': {
                content: '" "',
                position: 'absolute',
                top: '50%',
                left: '0.5%',
                borderBottom: '1px solid #9e9c9c',
                width: '99%',
              },
            }}
          />
        )}
      </Paper>
    </Box>
  );
};

export default AvaSuburbStatus;
